<template>
  <div class="users-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1>Users</h1>
      <div class="header-actions">
        <button v-if="store.getters['auth/canCreateUsers']" @click="showCreateUserForm = true" class="btn btn-primary">
          <i class="icon-user-plus"></i> Create User
        </button>
        <button @click="showInviteForm = true" class="btn btn-secondary">
          <i class="icon-envelope"></i> Send Invitation
        </button>
      </div>
    </div>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-if="success" class="success-message">
      {{ success }}
    </div>

    <!-- Create User Form (Admin only) -->
    <div v-if="showCreateUserForm" class="form-panel">
      <div class="panel-header">
        <h3>Create New User</h3>
        <button @click="showCreateUserForm = false" class="btn-close">
          <i class="icon-close"></i>
        </button>
      </div>
      
      <form @submit.prevent="createUserDirect" class="user-form">
        <div class="form-row">
          <div class="form-group">
            <label>Full Name *</label>
            <input
              v-model="createUserForm.name"
              type="text"
              placeholder="Enter full name"
              required
            >
          </div>
          <div class="form-group">
            <label>Email Address *</label>
            <input
              v-model="createUserForm.email"
              type="email"
              placeholder="Enter email address"
              required
            >
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Password *</label>
            <input
              v-model="createUserForm.password"
              type="password"
              placeholder="Enter password"
              required
              minlength="6"
            >
          </div>
          <div class="form-group">
            <label>Role *</label>
            <select v-model="createUserForm.role" required>
              <option value="">Select Role</option>
              <option value="user">User</option>
              <option value="moderator">Moderator</option>
              <option value="admin" v-if="store.getters['auth/isSuperAdmin']">Admin</option>
              <option value="superadmin" v-if="store.getters['auth/isSuperAdmin']">Super Admin</option>
            </select>
          </div>
        </div>
        <div class="form-group" v-if="store.getters['auth/isSuperAdmin']">
          <label>Organization *</label>
          <select v-model="createUserForm.org_id" required>
            <option value="">Select Organization</option>
            <option v-for="org in organizations" :key="org.id" :value="org.id">
              {{ org.name }}
            </option>
          </select>
        </div>
        <div class="checkbox-group">
          <label class="checkbox-label">
            <input type="checkbox" v-model="createUserForm.is_active">
            <span>Active User</span>
          </label>
        </div>
        <div class="form-actions">
          <button type="button" @click="showCreateUserForm = false" class="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary">
            Create User
          </button>
        </div>
      </form>
    </div>

    <!-- Edit User Form -->
    <div v-if="showEditUserForm" class="form-panel">
      <div class="panel-header">
        <h3>Edit User</h3>
        <button @click="cancelEditUser" class="btn-close">
          <i class="icon-close"></i>
        </button>
      </div>
      
      <form @submit.prevent="updateUser" class="user-form">
        <div class="form-row">
          <div class="form-group">
            <label>Full Name *</label>
            <input
              v-model="editUserForm.name"
              type="text"
              placeholder="Enter full name"
              required
            >
          </div>
          <div class="form-group">
            <label>Email Address *</label>
            <input
              v-model="editUserForm.email"
              type="email"
              placeholder="Enter email address"
              required
            >
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Role *</label>
            <select v-model="editUserForm.role" required>
              <option value="">Select Role</option>
              <option value="user">User</option>
              <option value="moderator">Moderator</option>
              <option value="admin" v-if="store.getters['auth/isSuperAdmin']">Admin</option>
              <option value="superadmin" v-if="store.getters['auth/isSuperAdmin']">Super Admin</option>
            </select>
          </div>
          <div class="form-group" v-if="store.getters['auth/isSuperAdmin']">
            <label>Organization *</label>
            <select v-model="editUserForm.org_id" required>
              <option value="">Select Organization</option>
              <option v-for="org in organizations" :key="org.id" :value="org.id">
                {{ org.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="checkbox-group">
          <label class="checkbox-label">
            <input type="checkbox" v-model="editUserForm.is_active">
            <span>Active User</span>
          </label>
        </div>
        <div class="form-actions">
          <button type="button" @click="cancelEditUser" class="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary">
            Update User
          </button>
        </div>
      </form>
    </div>

    <!-- Invite User Form -->
    <div v-if="showInviteForm" class="form-panel">
      <div class="panel-header">
        <h3>Invite New User</h3>
        <button @click="showInviteForm = false" class="btn-close">
          <i class="icon-close"></i>
        </button>
      </div>
      
      <form @submit.prevent="inviteUser" class="invite-form">
        <div class="form-row">
          <div class="form-group">
            <label>Email *</label>
            <input
              v-model="inviteForm.email"
              type="email"
              placeholder="user@example.com"
              required
            >
          </div>
          <div class="form-group">
            <label>Role *</label>
            <select v-model="inviteForm.role" required>
              <option value="">Select Role</option>
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ role.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="form-actions">
          <button type="button" @click="showInviteForm = false" class="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary">
            Send Invitation
          </button>
        </div>
      </form>
    </div>

    <!-- Users List -->
    <div class="users-section">
      <div v-if="users.length === 0" class="empty-state">
        <i class="icon-users"></i>
        <h4>No Users Found</h4>
        <p>No users have been loaded yet. Try refreshing the page or creating a new user.</p>
      </div>
      
      <div v-else class="users-grid">
        <div 
          v-for="user in users" 
          :key="user.id || user._id" 
          class="user-card"
        >
          <div class="user-avatar">
            <i class="icon-user"></i>
          </div>
          <div class="user-info">
            <h4>{{ user.name || 'Unnamed User' }}</h4>
            <p class="user-email">{{ user.email || 'No email' }}</p>
            <div class="user-meta">
              <span class="user-role">{{ getRoleName(user.role_id) }}</span>
              <span class="user-status" :class="{ 'active': user.is_active }">
                {{ user.is_active ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <p class="user-joined">Joined {{ formatDate(user.created_at) }}</p>
          </div>
          <div class="user-actions">
            <button @click="editUser(user)" class="btn-icon" title="Edit">
              <i class="icon-edit"></i>
            </button>
            <button @click="removeUser(user.id)" class="btn-icon danger" title="Remove">
              <i class="icon-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.users-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: white;
  border-bottom: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.page-header h1 {
  margin: 0;
  color: #2c3e50;
  font-size: 2rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.error-message,
.success-message {
  padding: 0.75rem 2rem;
  margin: 0;
  font-weight: 500;
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  border-bottom: 1px solid #f1aeb5;
}

.success-message {
  background: #d1e7dd;
  color: #0f5132;
  border-bottom: 1px solid #a3cfbb;
}

.form-panel {
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 2rem;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.panel-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f8f9fa;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: #e9ecef;
  color: #495057;
}

.user-form,
.invite-form {
  max-width: 800px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #6f42c1;
  box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.1);
}

.checkbox-group {
  margin-bottom: 1.5rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: #2c3e50;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #6f42c1;
  color: white;
}

.btn-primary:hover {
  background: #5a2d91;
}

.btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.users-section {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

.empty-state {
  text-align: center;
  color: #6c757d;
  padding: 4rem 2rem;
}

.empty-state i {
  font-size: 5rem;
  margin-bottom: 1.5rem;
  opacity: 0.3;
  color: #6f42c1;
}

.empty-state h4 {
  margin: 0 0 0.5rem;
  color: #495057;
  font-size: 1.25rem;
}

.empty-state p {
  margin: 0;
  font-size: 0.95rem;
}

.users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

.user-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s ease;
  position: relative;
}

.user-card:hover {
  border-color: #6f42c1;
  box-shadow: 0 4px 12px rgba(111, 66, 193, 0.1);
}

.user-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6f42c1, #5a2d91);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

.user-info h4 {
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

.user-email {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  color: #6c757d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.user-role {
  background: #f3e8ff;
  color: #6f42c1;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.user-status {
  background: #ffebee;
  color: #c62828;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.user-status.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.user-joined {
  margin: 0;
  font-size: 0.85rem;
  color: #6c757d;
}

.user-actions {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  border-color: #6f42c1;
  color: #6f42c1;
}

.btn-icon.danger:hover {
  border-color: #dc3545;
  color: #dc3545;
  background: #fff5f5;
}

/* Icons */
.icon-user-plus:before { content: '👤➕'; }
.icon-envelope:before { content: '✉️'; }
.icon-close:before { content: '✕'; }
.icon-users:before { content: '👥'; }
.icon-user:before { content: '👤'; }
.icon-edit:before { content: '✏️'; }
.icon-trash:before { content: '🗑️'; }

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .users-grid {
    grid-template-columns: 1fr;
  }

  .header-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'UsersView',
  setup() {
    const store = useStore()
    
    // Reactive state
    const error = ref('')
    const success = ref('')
    const showInviteForm = ref(false)
    const showCreateUserForm = ref(false)
    const showEditUserForm = ref(false)

    // Forms
    const inviteForm = reactive({
      email: '',
      role: ''
    })

    const createUserForm = reactive({
      name: '',
      email: '',
      password: '',
      role: '',
      org_id: '',
      is_active: true
    })

    const editUserForm = reactive({
      id: null,
      name: '',
      email: '',
      role: '',
      org_id: '',
      is_active: true,
      isEditing: false
    })

    // Computed properties
    const users = computed(() => {
      const rawUsers = store.state.users?.items || []
      return Array.isArray(rawUsers) ? rawUsers.filter(user => 
        user && 
        typeof user === 'object' &&
        (user.id || user._id) && 
        (user.email || user.name)
      ) : []
    })

    const roles = computed(() => store.state.roles?.items || [])
    
    const organizations = computed(() => {
      const rawOrgs = store.state.organizations?.items || []
      return Array.isArray(rawOrgs) ? rawOrgs.filter(org => org && typeof org === 'object' && org.id) : []
    })

    // Methods
    const fetchUsers = async () => {
      try {
        await store.dispatch('users/fetchAll')
      } catch (err) {
        console.error('Error fetching users:', err)
        error.value = 'Failed to load users. Please check your connection and try again.'
        setTimeout(() => error.value = '', 5000)
      }
    }

    const fetchRoles = async () => {
      try {
        await store.dispatch('roles/fetchAll')
      } catch (err) {
        console.error('Error fetching roles:', err)
      }
    }

    const fetchOrganizations = async () => {
      try {
        await store.dispatch('organizations/fetchAll')
      } catch (err) {
        console.error('Error fetching organizations:', err)
      }
    }

    const inviteUser = async () => {
      try {
        await store.dispatch('users/invite', {
          email: inviteForm.email,
          role_id: inviteForm.role
        })
        
        success.value = 'User invitation sent successfully!'
        setTimeout(() => success.value = '', 3000)
        
        inviteForm.email = ''
        inviteForm.role = ''
        showInviteForm.value = false
        
        await fetchUsers()
      } catch (err) {
        console.error('Error inviting user:', err)
        error.value = 'Failed to send invitation'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const createUserDirect = async () => {
      try {
        if (!createUserForm.name || !createUserForm.email || !createUserForm.password || !createUserForm.role) {
          error.value = 'Please fill in all required fields'
          setTimeout(() => error.value = '', 3000)
          return
        }

        // Set org_id to current user's org if superadmin hasn't selected one
        if (!createUserForm.org_id && !store.getters.isSuperAdmin) {
          createUserForm.org_id = store.state.user?.org_id
        }

        await store.dispatch('users/create', {
          name: createUserForm.name,
          email: createUserForm.email,
          password: createUserForm.password,
          role_id: createUserForm.role,
          org_id: createUserForm.org_id,
          is_active: createUserForm.is_active
        })

        success.value = 'User created successfully!'
        setTimeout(() => success.value = '', 3000)
        
        // Reset form
        createUserForm.name = ''
        createUserForm.email = ''
        createUserForm.password = ''
        createUserForm.role = ''
        createUserForm.org_id = ''
        createUserForm.is_active = true
        showCreateUserForm.value = false
        
        await fetchUsers()
      } catch (err) {
        console.error('Error creating user:', err)
        error.value = 'Failed to create user'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const editUser = (user) => {
      editUserForm.id = user.id
      editUserForm.name = user.name || ''
      editUserForm.email = user.email || ''
      editUserForm.role = user.role_id || user.role || ''
      editUserForm.org_id = user.org_id || ''
      editUserForm.is_active = user.is_active !== undefined ? user.is_active : true
      editUserForm.isEditing = true
      showEditUserForm.value = true
    }

    const updateUser = async () => {
      try {
        if (!editUserForm.name || !editUserForm.email || !editUserForm.role) {
          error.value = 'Please fill in all required fields'
          setTimeout(() => error.value = '', 3000)
          return
        }

        // Set org_id to current user's org if superadmin hasn't selected one
        if (!editUserForm.org_id && !store.getters.isSuperAdmin) {
          editUserForm.org_id = store.state.user?.org_id
        }

        await store.dispatch('updateUser', {
          id: editUserForm.id,
          updates: {
            name: editUserForm.name,
            email: editUserForm.email,
            role_id: editUserForm.role,
            org_id: editUserForm.org_id,
            is_active: editUserForm.is_active
          }
        })
        success.value = 'User updated successfully!'
        setTimeout(() => success.value = '', 3000)
        
        cancelEditUser()
        await fetchUsers()
      } catch (err) {
        console.error('Error updating user:', err)
        error.value = 'Failed to update user'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const cancelEditUser = () => {
      editUserForm.id = null
      editUserForm.name = ''
      editUserForm.email = ''
      editUserForm.role = ''
      editUserForm.org_id = ''
      editUserForm.is_active = true
      editUserForm.isEditing = false
      showEditUserForm.value = false
    }

    const removeUser = async (userId) => {
      if (!confirm('Are you sure you want to remove this user?')) return
      
      try {
        await store.dispatch('users/delete', userId)
        success.value = 'User removed successfully!'
        setTimeout(() => success.value = '', 3000)
        await fetchUsers()
      } catch (err) {
        console.error('Error removing user:', err)
        error.value = 'Failed to remove user'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const getRoleName = (roleId) => {
      const role = roles.value.find(r => r.id === roleId)
      return role?.name || 'Unknown Role'
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      try {
        return new Date(dateString).toLocaleDateString()
      } catch (e) {
        return 'Invalid Date'
      }
    }

    // Lifecycle
    onMounted(async () => {
      try {
        await Promise.all([
          fetchUsers(),
          fetchRoles(),
          fetchOrganizations()
        ])
      } catch (error) {
        console.error('Error loading data:', error)
        error.value = 'Failed to load data'
        setTimeout(() => error.value = '', 3000)
      }
    })

    return {
      // Store
      store,
      
      // State
      error,
      success,
      showInviteForm,
      showCreateUserForm,
      showEditUserForm,
      inviteForm,
      createUserForm,
      editUserForm,
      
      // Computed
      users,
      roles,
      organizations,
      
      // Methods
      inviteUser,
      createUserDirect,
      editUser,
      updateUser,
      cancelEditUser,
      removeUser,
      getRoleName,
      formatDate
    }
  }
}
</script>